(() => {
  const MAX_COMPARISONS = 4
  const EXACT_VARIANT_ID = /^\d{14,17}$/

  const publishFuelFactsEvent = (eventName, data) => {
    try {
      return window.RSGV9Analytics?.publishFeatureEvent?.('fuelFacts', eventName, data)
    } catch (_) {
      return undefined
    }
  }

  const normaliseId = (value) => {
    const id = String(value ?? '').trim()
    return EXACT_VARIANT_ID.test(id) ? id : ''
  }

  const productAndVariant = (record) => record.variant ? `${record.product}, ${record.variant}` : record.product

  const readJson = (element, fallback = {}) => {
    if (!element) return fallback
    try {
      return JSON.parse(element.textContent)
    } catch (_) {
      return fallback
    }
  }

  const parseComparisonUrl = (search, catalogue) => {
    const params = new URLSearchParams(String(search || '').replace(/^\?/, ''))
    if (params.get('v') !== '1') return { ids: [], rejected: params.has('v') || params.has('p') }

    const requested = (params.get('p') || '').split(',').filter(Boolean)
    const ids = []
    let rejected = requested.length > MAX_COMPARISONS

    for (const candidate of requested) {
      const id = normaliseId(candidate)
      const record = id && catalogue[id]
      if (!record || ids.includes(id) || ids.length >= MAX_COMPARISONS) {
        rejected = true
        continue
      }
      ids.push(id)
    }
    return { ids, rejected }
  }

  const buildComparisonUrl = (ids) => {
    const safeIds = ids.map(normaliseId).filter(Boolean).slice(0, MAX_COMPARISONS)
    if (safeIds.length === 0) return '/pages/fuel-compare'
    return `/pages/fuel-compare?v=1&p=${safeIds.join(',')}`
  }

  const filterCatalogue = (catalogue, filters = {}) => {
    const query = String(filters.query || '').trim().toLowerCase()
    return Object.values(catalogue).filter((record) => {
      const haystack = `${record.product} ${record.variant} ${record.brand} ${record.format}`.toLowerCase()
      return (
        (!query || haystack.includes(query)) &&
        (!filters.brand || record.brand === filters.brand) &&
        (!filters.format || record.format === filters.format) &&
        (!filters.caffeine || record.caffeineFilter === filters.caffeine)
      )
    })
  }

  const productKey = (record) => String(record?.productUrl || '').split('?')[0] || `${record?.brand || ''}:${record?.product || ''}`

  const groupCatalogue = (records) => {
    const groups = new Map()
    records.forEach((record) => {
      const key = productKey(record)
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(record)
    })
    return [...groups.values()]
  }

  const nextSelection = (ids, variantId, selected) => {
    const id = normaliseId(variantId)
    if (!id) return { ids: [...ids], maximumReached: false }
    if (!selected) return { ids: ids.filter((candidate) => candidate !== id), maximumReached: false }
    if (ids.includes(id)) return { ids: [...ids], maximumReached: false }
    if (ids.length >= MAX_COMPARISONS) return { ids: [...ids], maximumReached: true }
    return { ids: [...ids, id], maximumReached: false }
  }

  const buildBuyOncePayload = (catalogue, variantId, quantity = 1) => {
    const id = normaliseId(variantId)
    const record = id && catalogue[id]
    if (!record || !record.available) throw new RangeError('Variant is not eligible')
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) throw new RangeError('Quantity is invalid')
    return { items: [{ id, quantity }] }
  }

  const requestBuyOnce = async (catalogue, variantId, quantity, fetcher = fetch) => {
    const payload = buildBuyOncePayload(catalogue, variantId, quantity)
    const response = await fetcher('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
    if (!response.ok) throw new Error('Cart request failed')
    const added = await response.json()
    const addedItems = Array.isArray(added?.items) ? added.items : [added]
    const addedItem = addedItems.find((item) => normaliseId(item?.variant_id) === normaliseId(variantId))
    const returnedQuantity = Number(addedItem?.quantity)
    if (!addedItem || !Number.isInteger(returnedQuantity) || returnedQuantity < quantity) {
      throw new Error('Cart response did not match request')
    }
    return added
  }

  const notifyCartAdded = (added, button) => {
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      bubbles: true,
      detail: { product: added, addToCartBtn: button, source: 'rsg-fuel-facts' }
    }))
  }

  const nodeById = (root, key, selector) => root.querySelector(selector) || document.getElementById?.(root.dataset[key])

  const syncPdpCommerceControls = (root, record) => {
    const detail = nodeById(root, 'detailId', '[data-rsg-fuel-facts-detail]')
    if (!detail) return false
    const labels = root.rsgFuelFactsLabels || {}
    const quantity = detail.querySelector('[data-rsg-fuel-facts-quantity]')
    const add = detail.querySelector('[data-rsg-fuel-facts-add]')
    const empty = detail.querySelector('[data-rsg-fuel-facts-empty]')
    const available = record?.available === true

    if (quantity) quantity.disabled = !available
    if (add) {
      add.disabled = !available
      add.textContent = available
        ? labels.addCart || 'Add to cart'
        : record
          ? labels.unavailable || 'Currently unavailable'
          : labels.missing || 'Fuel Facts unavailable'
    }
    if (empty) {
      empty.textContent = record
        ? labels.unavailableState || 'This option is currently unavailable. Choose another option to add to your cart.'
        : labels.missingState || 'Verified Fuel Facts are not available for this option, so these comparison controls are disabled. You can still use the product options above if this option is shown as available.'
      empty.hidden = available
    }
    root.dataset.currentVariantId = record?.id || ''
    root.dataset.currentVariantAvailable = available ? 'true' : 'false'
    return available
  }

  const factIcon = (label) => {
    const paths = {
      Carbohydrate: 'M13 2 5 13h6l-1 9 8-12h-6l1-8Z',
      Calories: 'M13 2c1 5-3 6-3 10 0 2 1 3 2 4-4-1-6-4-5-8-3 3-4 6-2 10 2 4 8 5 11 2 4-5 1-10-3-14Z',
      Sodium: 'M12 2S5 10 5 15a7 7 0 0 0 14 0c0-5-7-13-7-13Z',
      Salt: 'M12 2S5 10 5 15a7 7 0 0 0 14 0c0-5-7-13-7-13Z',
      Fluid: 'M12 2S5 10 5 15a7 7 0 0 0 14 0c0-5-7-13-7-13Z',
      Caffeine: 'M4 8h13v5a6 6 0 0 1-6 6H10a6 6 0 0 1-6-6V8Zm13 2h2a2 2 0 1 1 0 4h-2M7 3v2m4-2v2m4-2v2'
    }
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('aria-hidden', 'true')
    svg.setAttribute('focusable', 'false')
    path.setAttribute('d', paths[label] || 'M12 3v18M3 12h18')
    path.setAttribute('fill', 'none')
    path.setAttribute('stroke', 'currentColor')
    path.setAttribute('stroke-width', '1.8')
    path.setAttribute('stroke-linecap', 'round')
    path.setAttribute('stroke-linejoin', 'round')
    svg.append(path)
    return svg
  }

  const factNodes = (facts, className, icons = false) => facts.flatMap(({ label, value }) => {
    if (!value) return []
    const fact = document.createElement('div')
    fact.className = className
    const term = document.createElement('dt')
    const description = document.createElement('dd')
    term.textContent = label
    description.textContent = value
    if (icons) fact.append(factIcon(label))
    fact.append(term, description)
    return [fact]
  })

  const renderPdp = (root, catalogue, variantId) => {
    const record = catalogue[normaliseId(variantId)]
    const summary = nodeById(root, 'summaryId', '[data-rsg-fuel-facts-summary]')
    const detail = nodeById(root, 'detailId', '[data-rsg-fuel-facts-detail]')
    if (!summary || !detail) return false
    const status = detail.querySelector('[data-rsg-fuel-facts-status]')
    if (status) status.textContent = ''
    summary.hidden = !record
    detail.hidden = false
    detail.dataset.mobileExpanded = record ? 'false' : 'true'
    summary.setAttribute('aria-expanded', detail.dataset.mobileExpanded)

    const summaryValues = summary.querySelector('[data-rsg-fuel-facts-summary-values]')
    const values = detail.querySelector('[data-rsg-fuel-facts-values]')
    const basis = detail.querySelector('[data-rsg-fuel-facts-basis]')
    const basisRow = detail.querySelector('[data-rsg-fuel-facts-basis-row]')
    const notes = detail.querySelector('[data-rsg-fuel-facts-notes]')
    const compare = detail.querySelector('[data-rsg-fuel-facts-compare]')
    const product = detail.querySelector('[data-rsg-fuel-facts-product]')
    const variant = detail.querySelector('[data-rsg-fuel-facts-variant]')
    const price = detail.querySelector('[data-rsg-fuel-facts-price]')
    const availability = detail.querySelector('[data-rsg-fuel-facts-availability]')
    if (!summaryValues || !values || !basis || !notes || !product || !variant || !price || !availability) return false

    if (!record) {
      summaryValues.replaceChildren()
      values.replaceChildren()
      basis.textContent = ''
      if (basisRow) basisRow.hidden = true
      notes.textContent = ''
      notes.hidden = true
      product.textContent = ''
      variant.textContent = ''
      price.textContent = ''
      availability.textContent = root.rsgFuelFactsLabels?.missing || 'Fuel Facts unavailable'
      if (compare) compare.href = buildComparisonUrl([])
      syncPdpCommerceControls(root, null)
      return false
    }

    const summaryFacts = (record.summaryFacts || Object.entries(record.facts || {}).map(([label, value]) => ({ label, value })))
      .slice(0, 4)
      .map((fact) => fact.label === 'Caffeine' && fact.value === 'Caffeine-free' ? { ...fact, value: '0mg' } : fact)
    const details = Object.entries(record.facts || {}).map(([label, value]) => ({ label, value }))
    summaryValues.replaceChildren(...factNodes(summaryFacts, 'rsg-fuel-facts-summary__fact', true))
    values.replaceChildren(...factNodes(details, 'rsg-fuel-facts-detail__fact'))
    basis.textContent = record.basis
    if (basisRow) basisRow.hidden = !record.basis
    notes.textContent = record.notes || ''
    notes.hidden = !record.notes
    product.textContent = record.product
    variant.textContent = record.variant ? `— ${record.variant}` : ''
    price.textContent = record.price
    availability.textContent = ` · ${record.available ? root.rsgFuelFactsLabels?.available || 'Available' : root.rsgFuelFactsLabels?.unavailable || 'Currently unavailable'}`
    if (compare) compare.href = buildComparisonUrl([record.id])
    syncPdpCommerceControls(root, record)
    return true
  }

  const element = (tag, className, text) => {
    const node = document.createElement(tag)
    if (className) node.className = className
    if (text) node.textContent = text
    return node
  }

  const option = (value, text) => {
    const node = element('option', '', text)
    node.value = value
    return node
  }

  const unique = (records, field) => [...new Set(records.map((record) => record[field]))].sort()

  const imageProductRequests = new Map()
  const recoveredImageUrls = new Map()

  const currentImageProduct = (record) => {
    let url
    try {
      url = new URL(record.productUrl, window.location.origin)
      if (url.origin !== window.location.origin || !/^\/(?:[^/]+\/)?products\/[^/]+\/?$/.test(url.pathname)) return Promise.resolve(null)
    } catch (_) {
      return Promise.resolve(null)
    }
    const path = url.pathname.replace(/\/$/, '') + '.js'
    if (!imageProductRequests.has(path)) {
      imageProductRequests.set(path, fetch(path, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then((response) => response.ok ? response.json() : null).catch(() => null))
    }
    return imageProductRequests.get(path)
  }

  const productImage = (record, className) => {
    const image = element('img', className)
    image.alt = record.imageAlt || record.product
    image.loading = 'lazy'
    image.decoding = 'async'
    image.width = 320
    image.height = 320
    const key = String(record.id) + ':' + (record.imageUrl || '')
    const attempted = new Set()
    let alternatives
    let recovering = false

    const setSource = (source) => {
      try {
        const raw = typeof source === 'string' ? source : source?.src
        if (typeof raw !== 'string' || !raw.trim()) return false
        const url = new URL(raw, window.location.origin)
        if (!['https:', 'http:'].includes(url.protocol) || attempted.has(url.href)) return false
        attempted.add(url.href)
        image.src = url.href
        return true
      } catch (_) {
        return false
      }
    }

    const recover = async () => {
      if (recovering) return
      recovering = true
      if (!alternatives) {
        const product = await currentImageProduct(record)
        const variant = product?.variants?.find((item) => String(item.id) === String(record.id))
        alternatives = [variant?.featured_image, product?.featured_image]
      }
      recovering = false
      while (alternatives.length) {
        if (setSource(alternatives.shift())) return
      }
      // Keep the product name and controls usable if Shopify has no working photo.
      recoveredImageUrls.set(key, '')
      image.hidden = true
      image.removeAttribute('src')
    }

    image.addEventListener('error', recover)
    image.addEventListener('load', () => {
      if (alternatives) recoveredImageUrls.set(key, image.src)
    })
    if (recoveredImageUrls.has(key) && !recoveredImageUrls.get(key)) {
      image.hidden = true
    } else if (!setSource(recoveredImageUrls.get(key) || record.imageUrl)) {
      recover()
    }
    return image
  }

  const placePdp = (root) => {
    const section = root.closest('section.page-content--product')
    if (!section) return
    const summary = nodeById(root, 'summaryId', '[data-rsg-fuel-facts-summary]')
    const detail = nodeById(root, 'detailId', '[data-rsg-fuel-facts-detail]')
    const media = section.querySelector(
      '.product-grid__container > .grid__item.product-single__sticky:not(.product-grid__content)'
    )
    if (!summary || !detail) return
    if (media) media.append(summary)
    section.insertAdjacentElement('afterend', detail)
  }

  const bootPdp = (root) => {
    if (root.dataset.rsgFuelFactsReady === 'true') return
    const catalogue = readJson(root.querySelector('[data-rsg-fuel-facts-catalogue]'))
    root.rsgFuelFactsLabels = readJson(root.querySelector('[data-rsg-fuel-facts-labels]'))
    const variantEvent = `variant:change:${root.dataset.sectionId}:${root.dataset.productId}`
    const place = () => placePdp(root)
    const tailwindOption = root.closest('.rsg-tailwind-pdp')?.querySelector('[data-tailwind-variant]')?.selectedOptions?.[0]
    const initialVariantId = tailwindOption ? tailwindOption.value : root.dataset.initialVariantId
    const initialRecord = catalogue[normaliseId(initialVariantId)]
    const caffeineMatches = !tailwindOption || initialRecord?.caffeineFilter === (tailwindOption.dataset.caffeine === '1' ? 'contains' : 'free')
    renderPdp(root, catalogue, caffeineMatches ? initialVariantId : '')
    place()
    const summary = nodeById(root, 'summaryId', '[data-rsg-fuel-facts-summary]')
    const detail = nodeById(root, 'detailId', '[data-rsg-fuel-facts-detail]')
    summary?.addEventListener('click', (event) => {
      if (!window.matchMedia('(max-width: 768px)').matches || !detail || detail.hidden) return
      event.preventDefault()
      detail.dataset.mobileExpanded = 'true'
      summary.setAttribute('aria-expanded', 'true')
      detail.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    })
    const quantity = detail?.querySelector('[data-rsg-fuel-facts-quantity]')
    const add = detail?.querySelector('[data-rsg-fuel-facts-add]')
    const status = detail?.querySelector('[data-rsg-fuel-facts-status]')
    add?.addEventListener('click', async () => {
      const id = root.dataset.currentVariantId
      const requested = Number(quantity?.value)
      const record = catalogue[id]
      if (!record?.available) {
        syncPdpCommerceControls(root, record)
        if (status) status.textContent = record
          ? root.rsgFuelFactsLabels.unavailableState
          : root.rsgFuelFactsLabels.missingState
        return
      }
      try {
        buildBuyOncePayload(catalogue, id, requested)
        add.disabled = true
        add.textContent = root.rsgFuelFactsLabels.adding
        const added = await requestBuyOnce(catalogue, id, requested)
        publishFuelFactsEvent('product_added_to_cart', {
          context: 'pdp',
          variant_id: id,
          quantity: requested
        })
        notifyCartAdded(added, add)
        if (status && root.dataset.currentVariantId === id) status.textContent = root.rsgFuelFactsLabels.added
      } catch (_) {
        if (status && root.dataset.currentVariantId === id) status.textContent = root.rsgFuelFactsLabels.addError
      } finally {
        syncPdpCommerceControls(root, catalogue[root.dataset.currentVariantId])
      }
    })
    document.addEventListener(
      variantEvent,
      (event) => {
        if (event.detail?.loading && !event.detail.html) event.detail.html = document
        if (detail) detail.dataset.mobileExpanded = 'false'
        renderPdp(root, catalogue, event.detail?.variant?.id)
      },
      { capture: true }
    )
    root.dataset.rsgFuelFactsReady = 'true'
  }

  const bootComparison = (root) => {
    if (root.dataset.rsgFuelCompareReady === 'true') return
    const catalogue = { ...readJson(root.querySelector('[data-rsg-fuel-facts-catalogue]')), ...readJson(root.querySelector('[data-rsg-fuel-facts-additions]')) }
    const labels = readJson(root.querySelector('[data-rsg-fuel-compare-labels]'))
    const records = Object.values(catalogue)
    const search = root.querySelector('[data-rsg-compare-search]')
    const brand = root.querySelector('[data-rsg-compare-brand]')
    const format = root.querySelector('[data-rsg-compare-format]')
    const caffeine = root.querySelector('[data-rsg-compare-caffeine]')
    const catalogueRoot = root.querySelector('[data-rsg-compare-catalogue]')
    const table = root.querySelector('[data-rsg-compare-table]')
    const tableWrap = root.querySelector('[data-rsg-compare-table-wrap]')
    const empty = root.querySelector('[data-rsg-compare-empty]')
    const status = root.querySelector('[data-rsg-compare-status]')
    const returnLink = root.querySelector('[data-rsg-compare-return]')
    const selectionDock = root.querySelector('[data-rsg-compare-selection-dock]')
    const selectionCount = root.querySelector('[data-rsg-compare-selection-count]')
    const selectionStatus = root.querySelector('[data-rsg-compare-selection-status]')
    const viewSelection = root.querySelector('[data-rsg-compare-view-selection]')
    if (!search || !brand || !format || !caffeine || !catalogueRoot || !table || !tableWrap || !empty || !status || !returnLink || !selectionDock || !selectionCount || !selectionStatus || !viewSelection) return

    unique(records, 'brand').forEach((value) => brand.append(option(value, value)))
    unique(records, 'format').forEach((value) => format.append(option(value, value)))

    let selectedIds = []
    const variantChoices = new Map()
    const cardFeedback = new Map()

    const announce = (message) => {
      status.textContent = message || ''
    }

    const announceSelection = (message) => {
      selectionStatus.textContent = message || ''
    }

    const updateSelectionDock = () => {
      selectionDock.hidden = selectedIds.length === 0
      selectionCount.textContent = `${selectedIds.length} ${selectedIds.length === 1 ? labels.selectedOne : labels.selectedMany}`
    }

    let originId = ''

    const updateReturnLink = () => {
      const origin = catalogue[originId]
      returnLink.hidden = !origin
      if (origin) returnLink.href = origin.productUrl
    }

    const addToCart = async (record, quantity, button) => {
      const original = button.textContent
      button.disabled = true
      button.textContent = labels.adding
      try {
        const added = await requestBuyOnce(catalogue, record.id, quantity)
        publishFuelFactsEvent('product_added_to_cart', {
          context: 'comparison',
          variant_id: record.id,
          quantity
        })
        button.textContent = labels.added
        announce(`${record.product}: ${labels.added}`)
        notifyCartAdded(added, button)
      } catch (_) {
        announce(labels.addError)
        button.textContent = original
      } finally {
        button.disabled = false
      }
    }

    const buyOnceControls = (record) => {
      const form = element('div', 'rsg-fuel-compare__buy')
      const id = `rsg-compare-quantity-${record.id}`
      const label = element('label', 'visually-hidden', `${labels.quantity}: ${record.product}`)
      label.htmlFor = id
      const quantity = element('input', 'rsg-fuel-compare__quantity')
      quantity.id = id
      quantity.type = 'number'
      quantity.min = '1'
      quantity.max = '99'
      quantity.step = '1'
      quantity.value = '1'
      quantity.inputMode = 'numeric'
      quantity.disabled = !record.available
      const button = element(
        'button',
        'rsg-v9-button rsg-fuel-compare__add-cart',
        record.available ? labels.addCart : labels.unavailable
      )
      button.type = 'button'
      button.disabled = !record.available
      button.addEventListener('click', () => {
        const requested = Number(quantity.value)
        try {
          buildBuyOncePayload(catalogue, record.id, requested)
          addToCart(record, requested, button)
        } catch (_) {
          announce(labels.addError)
        }
      })
      form.append(label, quantity, button)
      return form
    }

    const createCatalogueCard = (group) => {
      const key = productKey(group[0])
      const selectedRecord = group.find((candidate) => selectedIds.includes(candidate.id))
      const rememberedRecord = group.find((candidate) => candidate.id === variantChoices.get(key))
      const record = selectedRecord || rememberedRecord || group.find((candidate) => candidate.available) || group[0]
      variantChoices.set(key, record.id)
      const card = element('article', 'rsg-fuel-compare__option')
      if (selectedIds.includes(record.id)) card.classList.add('is-selected')
      const image = productImage(record, 'rsg-fuel-compare__option-image')
      const body = element('div', 'rsg-fuel-compare__option-body')
      const brandName = element('p', 'rsg-fuel-compare__brand', record.brand)
      const heading = element('h3', '', record.product)
      let variant = element('p', 'rsg-fuel-compare__variant', record.variant)
      if (group.length > 1) {
        const picker = element('label', 'rsg-fuel-compare__variant-picker')
        const pickerLabel = element('span', '', labels.chooseVersion)
        const select = element('select', 'rsg-fuel-compare__variant-select')
        group.forEach((candidate) => select.append(option(candidate.id, candidate.variant)))
        select.value = record.id
        select.addEventListener('change', () => {
          const nextId = normaliseId(select.value)
          const previousSelected = group.find((candidate) => selectedIds.includes(candidate.id))
          variantChoices.set(key, nextId)
          if (previousSelected && nextId && catalogue[nextId]?.available) {
            selectedIds = selectedIds.map((id) => id === previousSelected.id ? nextId : id)
            history.pushState({}, '', buildComparisonUrl(selectedIds))
            publishFuelFactsEvent('comparison_updated', {
              context: 'comparison',
              action: 'variant_changed',
              selected_count: selectedIds.length,
              variant_id: nextId
            })
          }
          renderAll()
        })
        picker.append(pickerLabel, select)
        variant = picker
      }
      const headline = element('p', '', record.headline)
      const basis = element('p', 'rsg-fuel-compare__basis', record.basis)
      const commerce = element('p', 'rsg-fuel-compare__commerce', `${record.price} · ${record.available ? labels.available : labels.unavailable}`)
      const choice = element('label', 'rsg-fuel-compare__choice')
      const checkbox = element('input')
      checkbox.type = 'checkbox'
      checkbox.value = record.id
      checkbox.checked = selectedIds.includes(record.id)
      checkbox.disabled = !record.available
      checkbox.setAttribute('aria-label', `${checkbox.checked ? labels.remove : labels.add}: ${productAndVariant(record)}`)
      choice.append(checkbox, document.createTextNode(checkbox.checked ? labels.remove : labels.add))
      body.append(brandName, heading, variant, headline, basis, commerce)
      card.append(image, body, choice)
      const feedbackMessage = cardFeedback.get(key)
      if (feedbackMessage) {
        const feedback = element('p', 'rsg-fuel-compare__option-feedback')
        feedback.setAttribute('role', 'status')
        feedback.append(document.createTextNode(`${feedbackMessage} `))
        const view = element('a', '', labels.viewComparison)
        view.href = viewSelection.getAttribute('href')
        feedback.append(view)
        card.append(feedback)
      }
      checkbox.addEventListener('change', () => {
        const withoutSibling = checkbox.checked
          ? selectedIds.filter((id) => productKey(catalogue[id]) !== key)
          : selectedIds
        const result = nextSelection(withoutSibling, record.id, checkbox.checked)
        if (result.maximumReached) {
          checkbox.checked = false
          cardFeedback.clear()
          cardFeedback.set(key, labels.maximumDetail)
          announce(labels.maximum)
          announceSelection(labels.maximumDetail)
          renderCatalogue()
          return
        }
        selectedIds = result.ids
        cardFeedback.clear()
        if (checkbox.checked) cardFeedback.set(key, labels.addedCompare)
        announceSelection(checkbox.checked ? labels.addedCompare : '')
        history.pushState({}, '', buildComparisonUrl(selectedIds))
        publishFuelFactsEvent('comparison_updated', {
          context: 'comparison',
          action: checkbox.checked ? 'selected' : 'removed',
          selected_count: selectedIds.length,
          variant_id: record.id
        })
        renderAll()
      })
      return card
    }

    const renderCatalogue = () => {
      const visible = filterCatalogue(catalogue, {
        query: search.value,
        brand: brand.value,
        format: format.value,
        caffeine: caffeine.value
      })
      const groups = groupCatalogue(visible)
      catalogueRoot.replaceChildren(...groups.map(createCatalogueCard))
      announce(groups.length ? `${groups.length} ${groups.length === 1 ? labels.result : labels.results}` : labels.noResults)
    }

    const addCell = (row, tag, text, scope) => {
      const cell = element(tag, '', text)
      if (scope) cell.scope = scope
      row.append(cell)
      return cell
    }

    const renderTable = () => {
      const selected = selectedIds.map((id) => catalogue[id]).filter(Boolean)
      updateSelectionDock()
      tableWrap.hidden = selected.length === 0
      empty.hidden = selected.length > 0
      table.replaceChildren()
      table.style.minWidth = selected.length > 1 ? `${160 + (selected.length * 220)}px` : '100%'
      tableWrap.classList.toggle('is-scrollable', selected.length > 1)
      updateReturnLink()
      if (!selected.length) return

      const head = element('thead')
      const caption = element('caption', 'visually-hidden', labels.tableCaption)
      const headRow = element('tr')
      addCell(headRow, 'th', labels.fact, 'col')
      selected.forEach((record) => {
        const cell = addCell(headRow, 'th', '', 'col')
        const product = element('div', 'rsg-fuel-compare__table-product')
        const identity = element('div', 'rsg-fuel-compare__table-identity')
        identity.append(
          productImage(record, 'rsg-fuel-compare__table-image'),
          element('span', 'rsg-fuel-compare__brand', record.brand),
          element('span', 'rsg-fuel-compare__product-name', record.product),
          element('span', 'rsg-fuel-compare__variant', record.variant),
          element('span', 'rsg-fuel-compare__commerce', `${record.price} · ${record.available ? labels.available : labels.unavailable}`)
        )
        const actions = element('div', 'rsg-fuel-compare__table-actions')
        const links = element('div', 'rsg-fuel-compare__table-links')
        const view = element('a', '', labels.view)
        view.href = record.productUrl
        const remove = element('button', 'rsg-fuel-compare__remove', labels.remove)
        remove.type = 'button'
        remove.setAttribute('aria-label', `${labels.remove}: ${productAndVariant(record)}`)
        remove.addEventListener('click', () => {
          selectedIds = selectedIds.filter((id) => id !== record.id)
          cardFeedback.clear()
          announceSelection('')
          history.pushState({}, '', buildComparisonUrl(selectedIds))
          publishFuelFactsEvent('comparison_updated', {
            context: 'comparison',
            action: 'removed',
            selected_count: selectedIds.length,
            variant_id: record.id
          })
          renderAll()
        })
        links.append(view, remove)
        actions.append(links, buyOnceControls(record))
        product.append(identity, actions)
        cell.append(product)
      })
      head.append(headRow)

      const body = element('tbody')
      const factNames = [...new Set(selected.flatMap((record) => Object.keys(record.facts || {})))]
      const rows = [
        [labels.serving, (record) => record.basis],
        ...factNames.map((name) => [name, (record) => record.facts?.[name]]),
        ...(selected.some((record) => record.notes) ? [[labels.notes, (record) => record.notes]] : [])
      ]
      rows.forEach(([label, valueFor]) => {
        const row = element('tr')
        addCell(row, 'th', label, 'row')
        selected.forEach((record) => addCell(row, 'td', valueFor(record) || labels.noValue))
        body.append(row)
      })
      table.append(caption, head, body)
    }

    const renderAll = () => {
      renderCatalogue()
      renderTable()
    }

    const applyLocation = () => {
      const parsed = parseComparisonUrl(window.location.search, catalogue)
      if (!originId && parsed.ids[0]) originId = parsed.ids[0]
      selectedIds = parsed.ids
      cardFeedback.clear()
      announceSelection('')
      renderAll()
      if (parsed.rejected) announce(labels.linkAdjusted)
    }

    for (const control of [search, brand, format, caffeine]) {
      control.addEventListener(control === search ? 'input' : 'change', renderCatalogue)
    }
    window.addEventListener('popstate', applyLocation)
    applyLocation()
    root.dataset.rsgFuelCompareReady = 'true'
  }

  const boot = (scope = document) => {
    scope.querySelectorAll('[data-rsg-fuel-facts]').forEach(bootPdp)
    scope.querySelectorAll('[data-rsg-fuel-compare]').forEach(bootComparison)
  }

  const api = { MAX_COMPARISONS, buildBuyOncePayload, buildComparisonUrl, filterCatalogue, groupCatalogue, nextSelection, parseComparisonUrl, requestBuyOnce, renderPdp, boot }
  window.RSGFuelFacts = api
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot())
  else boot()
})()

